import { v4 as uuidv4 } from "uuid";

/**
 * Converts a file to base64
 * @param file The file to convert to base64
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Generates a unique key for uploading a file
 * @param fileName The name of the file
 * @param organizationId The ID of the organization
 */
export function generateUploadKey(
  fileName: string,
  organizationId: string,
  type: "logo" | "certificate",
): string {
  return `organizations/${organizationId}/${type}/${uuidv4()}.${fileName.split(".").pop()}`;
}

/**
 * Resizes an image to the specified dimensions (ChatGPT)
 * @param file The file to resize
 * @param maxWidth The maximum width of the resized image
 * @param maxHeight The maximum height of the resized image
 * @param padding The padding to add to the image
 */
export function resizeImage(
  file: File,
  maxWidth: number = 200,
  maxHeight: number = 200,
  padding: number = 20,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          // Set canvas size to maxWidth and maxHeight
          canvas.width = maxWidth;
          canvas.height = maxHeight;

          // Calculate the resized dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          if (width > height) {
            width = maxWidth - padding * 2;
            height = Math.round((img.height * width) / img.width);
          } else {
            height = maxHeight - padding * 2;
            width = Math.round((img.width * height) / img.height);
          }

          // Center the image with padding
          const xOffset = (maxWidth - width) / 2;
          const yOffset = (maxHeight - height) / 2;

          // Fill the background with a transparent or white color
          ctx.fillStyle = "rgba(0,0,0,0)";
          ctx.fillRect(0, 0, maxWidth, maxHeight);

          // Draw the image centered with padding
          ctx.drawImage(img, xOffset, yOffset, width, height);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob from canvas"));
              }
            },
            file.type || "image/jpeg",
            0.9,
          );
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image"));
      };

      img.src = objectUrl;
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Validates an image file
 * @param file The File object to validate
 * @param maxSizeInMB The maximum size of the file in MB
 */
export function validateImageFile(
  file: File,
  maxSizeInMB: number = 2,
): { valid: boolean; error?: string } {
  if (!file.type.startsWith("image/")) {
    return { valid: false, error: "File must be an image" };
  }

  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    return {
      valid: false,
      error: `File size must be less than ${maxSizeInMB}MB`,
    };
  }

  return { valid: true };
}
